
import { useState, useEffect } from "react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EditPixelForm } from "./EditPixelForm";
import { PixelFormValues } from "./CreatePixelForm";
import { PixelInstallation } from "./PixelInstallation";
import { ScrollArea } from "@/components/ui/scroll-area";

interface EditPixelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pixel: {
    id: string;
    name: string;
    domain: string;
    organization_id?: string | null;
  };
}

export function EditPixelDialog({ open, onOpenChange, pixel }: EditPixelDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showInstallation, setShowInstallation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceKeepOpen, setForceKeepOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setShowInstallation(false);
    }
  }, [open]);

  useEffect(() => {
    if (isSubmitting && !open) {
      onOpenChange(true);
      setForceKeepOpen(true);
    }
  }, [isSubmitting, open, onOpenChange]);

  const onSubmit = async (data: PixelFormValues) => {
    if (!user) return;
    setIsSubmitting(true);
    setForceKeepOpen(true);

    const cleanDomain = data.domain
      .replace(/^https?:\/\//, '') // Remove http:// or https://
      .replace(/\/+$/, ''); // Remove trailing slashes

    try {
      // Preserve the organization_id when updating
      const { error } = await supabase
        .from('pixels')
        .update({
          name: data.name,
          domain: cleanDomain,
          // Keep the organization_id from the original pixel
          organization_id: pixel.organization_id
        })
        .eq('id', pixel.id);

      if (error) throw error;

      toast.success("Pixel updated successfully");
      queryClient.invalidateQueries({ queryKey: ['pixels'] });
      setShowInstallation(true);
    } catch (error) {
      toast.error("Failed to update pixel");
    } finally {
      setIsSubmitting(false);
      setForceKeepOpen(false);
    }
  };

  const testPixel = async () => {
    try {
      toast.success("Test request sent successfully!");
    } catch (error) {
      toast.error("Failed to test pixel");
    }
  };

  const handleOpenChange = (newOpenState: boolean) => {
    if (!newOpenState && (isSubmitting || forceKeepOpen)) {
      return;
    }
    
    if (!newOpenState && showInstallation) {
      setShowInstallation(false);
    }
    
    onOpenChange(newOpenState);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="w-[800px] max-w-[90vw]"
        preventCloseKeys={isSubmitting || forceKeepOpen}
      >
        <DialogHeader>
          <DialogTitle>{showInstallation ? 'Update Your Pixel Installation' : 'Edit Pixel'}</DialogTitle>
          <DialogDescription>
            {showInstallation ? 'Make sure your pixel code is up to date.' : 'Modify your pixel settings.'}
          </DialogDescription>
        </DialogHeader>

        {!showInstallation ? (
          <EditPixelForm 
            pixel={pixel}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        ) : (
          <ScrollArea className="max-h-[65vh]">
            <PixelInstallation 
              pixelId={pixel.id}
              onTestPixel={testPixel}
            />
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
